import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import BrandSymbol from '../ui/BrandSymbol';
import { ResponsiveContainer, ResponsiveContent, ResponsiveFooter } from '../ui/ResponsiveLayout';
import { useSessionPersistence } from '../../hooks/useSessionPersistence';

const states = [
  'Alabama','Alaska','Arizona','Arkansas','California','Colorado','Connecticut','Delaware','Florida','Georgia','Hawaii','Idaho','Illinois','Indiana','Iowa','Kansas','Kentucky','Louisiana','Maine','Maryland','Massachusetts','Michigan','Minnesota','Mississippi','Missouri','Montana','Nebraska','Nevada','New Hampshire','New Jersey','New Mexico','New York','North Carolina','North Dakota','Ohio','Oklahoma','Oregon','Pennsylvania','Rhode Island','South Carolina','South Dakota','Tennessee','Texas','Utah','Vermont','Virginia','Washington','West Virginia','Wisconsin','Wyoming'
];

const assetRanges = [
  '$50,000',
  '$100,000',
  '$200,000',
  '$300,000',
  '$400,000',
  '$500,000',
  '$600,000',
  '$700,000',
  '$800,000',
  '$900,000',
  '$999,999+'
];

const Frame126: React.FC = () => {
  const navigate = useNavigate();
  const { session, updateTaskProgress } = useSessionPersistence();

  const [stateSelected, setStateSelected] = useState('');
  const [assetSelected, setAssetSelected] = useState('');
  
  // Get the selection from Frame 123
  const frame123Selection = session.taskProgress['frame-123']?.answers?.selection;
  
  // Dynamic content based on Frame 123 selection
  const contentConfig = useMemo(() => {
    switch (frame123Selection) {
      case 'I already have a last will & testament':
        return {
          headline: "Having a will is a strong first step. We'll help you round out your estate plan with other essentials—personal, legal, and financial—that ensure nothing gets missed.",
          stateQuestion: "What state is your will based in?",
          assetQuestion: "What's the approximate value of your liquid assets (like cash, checking, savings, or stocks)?"
        };
      case "I don't have a will yet, but I've thought about how I'd like to be remembered":
        return {
          headline: "That's a meaningful place to begin. We'll help you turn your wishes into a plan that protects your loved ones and your legacy.",
          stateQuestion: "In which state do you currently reside?",
          assetQuestion: "What's the approximate value of your liquid assets? (Don't worry, a guess is fine)"
        };
      case "I don't know where to start":
        return {
          headline: "Starting is the hardest part, but that's what we're here for. We'll break things down into clear, simple steps—no prep needed.",
          stateQuestion: "In which state do you currently reside?",
          assetQuestion: "What's the approximate value of your liquid assets? (Don't worry, a guess is fine)"
        };
      default:
        return {
          headline: "No problem. End of an Era can help you do things right.",
          stateQuestion: "In which state do you currently reside?",
          assetQuestion: "What's the approximate value of your liquid assets? (Don't worry, a guess is fine)"
        };
    }
  }, [frame123Selection]);
  
  // Load saved selections from session
  useEffect(() => {
    const savedProgress = session.taskProgress['frame-126'];
    if (savedProgress?.answers) {
      if (savedProgress.answers.stateSelected) {
        setStateSelected(savedProgress.answers.stateSelected);
      }
      if (savedProgress.answers.assetSelected) {
        setAssetSelected(savedProgress.answers.assetSelected);
      }
    }
  }, [session]);
  
  const isComplete = Boolean(stateSelected && assetSelected);

  const handleNext = async () => {
    if (isComplete) {
      // Save selections to session
      await updateTaskProgress('frame-126', {
        stateSelected,
        assetSelected,
        frame123Selection // Keep reference to original selection
      }, 'complete');
    }
    navigate('/frame-129');
  };

  const handleBack = () => {
    navigate('/frame-123');
  };

  return (
    <ResponsiveContainer background="stone">
      <ResponsiveContent>
      {/* Bottom gradient & spacer */}
      <div className="pointer-events-none absolute inset-x-0 bottom-0">
        <div className="h-32 bg-stone-50" />
        <div className="h-14 -mt-14 bg-gradient-to-b from-stone-50/0 to-stone-50" />
      </div>

      {/* Centered dark logo */}
        <div className="flex flex-col items-center w-full">
          <BrandSymbol size="3xl" showText={true} variant="dark" className="mx-auto mb-8 sm:mb-10 lg:mb-12" />

      {/* Main copy */}
          <div className="flex flex-col items-start w-full max-w-xs sm:max-w-md lg:max-w-2xl xl:max-w-4xl mx-auto">
            <h2 className="font-display text-lg sm:text-xl lg:text-2xl font-semibold text-green-700 mb-4 sm:mb-5 lg:mb-6 leading-relaxed w-full">
              {contentConfig.headline}
            </h2>

        {/* State question */}
            <p className="font-sans text-base sm:text-lg font-medium text-stone-800 mb-3 w-full">
              {contentConfig.stateQuestion}
            </p>
        <select
          value={stateSelected}
          onChange={(e) => {
            setStateSelected(e.target.value);
            // Auto-save selection on change
            updateTaskProgress('frame-126', {
              stateSelected: e.target.value,
              assetSelected,
              frame123Selection
            }, 'in-progress');
          }}
              className={`w-full sm:w-64 lg:w-80 h-11 sm:h-9 bg-white border border-neutral-200 rounded px-3 font-sans text-base mb-6 sm:mb-8 lg:mb-10 ${stateSelected ? 'text-stone-800' : 'text-stone-500'}`}
        >
          <option value="" disabled>Select</option>
          {states.map((s) => (
            <option key={s} value={s}>
              {s}
            </option>
          ))}
        </select>

        {/* Assets question */}
            <p className="font-sans text-base sm:text-lg font-medium text-stone-800 mb-3 w-full">
              {contentConfig.assetQuestion}
            </p>
        <select
          value={assetSelected}
          onChange={(e) => {
            setAssetSelected(e.target.value);
            // Auto-save selection on change
            updateTaskProgress('frame-126', {
              stateSelected,
              assetSelected: e.target.value,
              frame123Selection
            }, 'in-progress');
          }}
              className={`w-full sm:w-64 lg:w-80 h-11 sm:h-9 bg-white border border-neutral-200 rounded px-3 font-sans text-base mb-6 sm:mb-8 lg:mb-10 ${assetSelected ? 'text-stone-800' : 'text-stone-500'}`}
        >
          <option value="" disabled>Select</option>
          {assetRanges.map((r) => (
            <option key={r} value={r}>
              {r}
            </option>
          ))}
        </select>
          </div>

          {/* Bottom spacer to ensure footer is always accessible */}
          <div className="h-20 sm:h-32 lg:h-40" />
        </div>
      </ResponsiveContent>

      {/* ResponsiveFooter matching Frame 190 pattern */}
      <ResponsiveFooter
        title="Welcome"
        subtitle="Continuing your estate plan"
        progressPercent={66}
        onNext={handleNext}
        onBack={handleBack}
        nextDisabled={!isComplete}
        nextText="Next →"
        backText="← Back"
      />
    </ResponsiveContainer>
  );
};

export default Frame126; 